import { useEffect, useMemo, useState } from "react"

import { ApolloError } from "@apollo/client"
import { Cross1Icon } from "@radix-ui/react-icons"
import { FormProvider, useForm } from "react-hook-form"

import styles from "./CompanySmsSendingRuleEditV2.module.scss"
import { CompanySmsSendingRuleRowEditV2 } from "./CompanySmsSendingRuleRowEditV2"
import { buildDefaultCancelTypeSettingInputs } from "./defaults"
import {
	prepareCreateInput,
	prepareUpdateInput,
	processFormInput,
	toEncouragingRereservationUnitInputs,
	toManuallyClaimingUnitInputs,
} from "./helpers"
import { useZeroThreeShortcut } from "./useZeroThreeShortcut"

import {
	useSouhlAdminMutation,
	useSouhlAdminQuery,
} from "@/provider/apollo/Apollo"
import {
	companySmsSendingRuleModalMode,
	CompanySmsSendingRuleModalMode,
} from "@/souhl_admin/components/features/companies/sms-sending-rule/const"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"
import { getBadRequestErrorMessages } from "@/souhl_admin/lib/graphqlErrorHelpers"
import {
	companySmsSendingRuleV2EditResolver,
	CompanySmsSendingRuleV2EditType,
} from "@/souhl_admin/utils/form/companySmsSendingRuleV2Edit"

import { cancelFeeTypeForNonZeroYenReservationKey } from "../companySmsSendingRuleV2/CompanySmsSendingRuleV2.const"

const updateCompanySmsSendingRuleEditV2Mutation = graphql(/* GraphQL */ `
	mutation updateCompanySmsSendingRuleV2(
		$input: UpdateCompanySmsSendingRuleV2Input!
	) {
		updateCompanySmsSendingRuleV2(input: $input) {
			rule {
				id
			}
		}
	}
`)

const createCompanySmsSendingRuleV2Mutation = graphql(/* GraphQL */ `
	mutation createCompanySmsSendingRuleV2(
		$input: CreateCompanySmsSendingRuleV2Input!
	) {
		createCompanySmsSendingRuleV2(input: $input) {
			rule {
				id
			}
		}
	}
`)

type Props = {
	companyId: string
	ruleId?: string
	ruleIdForPolicyNamesFetch?: string // CREATE モードで policy names を取得するための rule ID
	mode: CompanySmsSendingRuleModalMode
	closeModal: () => void
	onUpdated: () => void
}

const companySmsSendingRuleEditV2Query = graphql(/* GraphQL */ `
	query getCompanySmsSendingRulesV2ForEdit($companyId: String!) {
		companySmsSendingRulesV2(companyId: $companyId) {
			rules {
				id
				name
				cancelPolicyTemplateType
				selectableClaimingConversionPolicyNames
				selectableEncouragingRereservationPolicyNames
				cancelTypeSettings {
					id
					cancelType
					cancelFeeAmountForNonZeroYenReservation
					cancelFeeAmountForZeroYenReservation
					cancelFeePercentForNonZeroYenReservation
					claimingConversionPolicyName
					encouragingRereservationPolicyName
					encouragingRereservationUnits {
						minVisitCount
						encouragingRereservationPolicyName
					}
					manuallyClaimingUnits {
						minVisitCount
						manuallyClaimingUnitKind
						conversionDays
					}
					minVisitCountForClaimingConversion
					minVisitCountForEncouragingRereservation
					minVisitCountForManuallyClaimingCancelFee
					minVisitCountForNothing
				}
			}
		}
	}
`)

export const CompanySmsSendingRuleEditV2 = ({
	companyId,
	ruleId,
	ruleIdForPolicyNamesFetch,
	mode,
	closeModal,
	onUpdated,
}: Props) => {
	const { data, refetch } = useSouhlAdminQuery(
		companySmsSendingRuleEditV2Query,
		{
			variables: { companyId },
		}
	)

	const methods = useForm<CompanySmsSendingRuleV2EditType>({
		resolver: companySmsSendingRuleV2EditResolver,
	})

	useEffect(() => {
		const rules = data?.companySmsSendingRulesV2?.rules || []
		const baseRule = rules.find((r) => r.id === ruleId)

		if (mode === companySmsSendingRuleModalMode.EDIT) {
			// 編集モード: 既存のルールを読み込む
			if (!baseRule) return
			methods.reset({
				id: baseRule.id,
				// TODO: 重複なくす
				name: baseRule.name,
				cancelTypeSettingInputs: baseRule.cancelTypeSettings.map((row) => ({
					id: row.id,
					cancelType: row.cancelType,
					cancelFeeTypeForNonZeroYenReservation:
						row.cancelFeePercentForNonZeroYenReservation != null
							? cancelFeeTypeForNonZeroYenReservationKey.PERCENT
							: cancelFeeTypeForNonZeroYenReservationKey.AMOUNT,
					cancelFeeAmountForNonZeroYenReservation:
						row.cancelFeeAmountForNonZeroYenReservation ?? 0,
					cancelFeeAmountForZeroYenReservation:
						row.cancelFeeAmountForZeroYenReservation,
					cancelFeePercentForNonZeroYenReservation:
						row.cancelFeePercentForNonZeroYenReservation ?? 0,
					claimingConversionPolicyName: row.claimingConversionPolicyName,
					encouragingRereservationPolicyName:
						row.encouragingRereservationPolicyName,
					encouragingRereservationUnits: toEncouragingRereservationUnitInputs(
						row.encouragingRereservationUnits
					),
					manuallyClaimingUnits: toManuallyClaimingUnitInputs(
						row.manuallyClaimingUnits
					),
					minVisitCountForClaimingConversion:
						row.minVisitCountForClaimingConversion,
					minVisitCountForEncouragingRereservation:
						row.minVisitCountForEncouragingRereservation,
					minVisitCountForManuallyClaimingCancelFee:
						row.minVisitCountForManuallyClaimingCancelFee,
					minVisitCountForNothing: row.minVisitCountForNothing,
				})),
			})
		} else if (mode === companySmsSendingRuleModalMode.DUPLICATE_AND_CREATE) {
			// 複製して作成モード: 既存のルールをベースに新規作成（IDは空、名前も変更）
			if (!baseRule) return
			methods.reset({
				id: "", // 新規作成なのでIDは空
				name: `${baseRule.name}_コピー`, // 名前にコピーを追加
				cancelPolicyTemplateType: baseRule.cancelPolicyTemplateType,
				cancelTypeSettingInputs: baseRule.cancelTypeSettings.map((row) => ({
					id: "", // 新規作成なのでIDは空
					cancelType: row.cancelType,
					cancelFeeTypeForNonZeroYenReservation:
						row.cancelFeePercentForNonZeroYenReservation != null
							? cancelFeeTypeForNonZeroYenReservationKey.PERCENT
							: cancelFeeTypeForNonZeroYenReservationKey.AMOUNT,
					cancelFeeAmountForNonZeroYenReservation:
						row.cancelFeeAmountForNonZeroYenReservation ?? 0,
					cancelFeeAmountForZeroYenReservation:
						row.cancelFeeAmountForZeroYenReservation,
					cancelFeePercentForNonZeroYenReservation:
						row.cancelFeePercentForNonZeroYenReservation ?? 0,
					claimingConversionPolicyName: row.claimingConversionPolicyName,
					encouragingRereservationPolicyName:
						row.encouragingRereservationPolicyName,
					encouragingRereservationUnits: toEncouragingRereservationUnitInputs(
						row.encouragingRereservationUnits
					),
					manuallyClaimingUnits: toManuallyClaimingUnitInputs(
						row.manuallyClaimingUnits
					),
					minVisitCountForClaimingConversion:
						row.minVisitCountForClaimingConversion,
					minVisitCountForEncouragingRereservation:
						row.minVisitCountForEncouragingRereservation,
					minVisitCountForManuallyClaimingCancelFee:
						row.minVisitCountForManuallyClaimingCancelFee,
					minVisitCountForNothing: row.minVisitCountForNothing,
				})),
			})
		} else {
			// create mode: 完全に新規のデフォルト値で初期化
			methods.reset({
				id: "",
				name: "",
				cancelTypeSettingInputs: buildDefaultCancelTypeSettingInputs(),
			})
		}
	}, [data, ruleId, methods, mode])

	useZeroThreeShortcut(methods, mode)

	const [errorMessages, setErrorMessages] = useState<string[]>([])

	const [mutateUpdate, { loading: updating }] = useSouhlAdminMutation(
		updateCompanySmsSendingRuleEditV2Mutation,
		{
			context: {
				disableErrorDialog: true,
			},
		}
	)
	const [mutateCreate, { loading: creating }] = useSouhlAdminMutation(
		createCompanySmsSendingRuleV2Mutation,
		{
			context: {
				disableErrorDialog: true,
			},
		}
	)

	const onSubmit = async (input: CompanySmsSendingRuleV2EditType) => {
		// エラーメッセージをクリア
		setErrorMessages([])

		// フォーム入力を処理
		const processedInput = processFormInput(input)

		if (mode === companySmsSendingRuleModalMode.EDIT) {
			// UPDATE用のinputを準備
			const updateInput = prepareUpdateInput(processedInput, companyId)
			await mutateUpdate({
				variables: {
					input: updateInput,
				},
				onCompleted: () => {
					closeModal()
					alert("配信ルールを更新しました")
					onUpdated()
					refetch()
				},
				onError: (error: ApolloError) => {
					const messages = getBadRequestErrorMessages(error)
					if (messages.length > 0) {
						setErrorMessages(messages)
					} else {
						setErrorMessages(["配信ルールの更新に失敗しました"])
					}
				},
			})
			return
		}

		// CREATE用のinputを準備
		const createInput = prepareCreateInput(processedInput, companyId)
		await mutateCreate({
			variables: {
				input: createInput,
			},
			onCompleted: () => {
				closeModal()
				alert("配信ルールを作成しました")
				onUpdated()
				refetch()
			},
			onError: (error: ApolloError) => {
				const messages = getBadRequestErrorMessages(error)
				if (messages.length > 0) {
					setErrorMessages(messages)
				} else {
					setErrorMessages(["配信ルールの作成に失敗しました"])
				}
			},
		})
	}

	const rule = useMemo(() => {
		const rules = data?.companySmsSendingRulesV2?.rules || []
		// CREATE モードの場合は ruleIdForPolicyNamesFetch を使用
		// それ以外の場合は通常の ruleId を使用
		const targetRuleId =
			mode === companySmsSendingRuleModalMode.CREATE
				? ruleIdForPolicyNamesFetch
				: ruleId
		return rules.find((r) => r.id === targetRuleId)
	}, [data, ruleId, ruleIdForPolicyNamesFetch, mode])

	const rows = methods.watch("cancelTypeSettingInputs") || []

	return (
		<form
			className={styles.company_sms_sending_rule}
			onSubmit={methods.handleSubmit(onSubmit)}
		>
			<FormProvider {...methods}>
				<div className={styles.close_button_container}>
					<button
						type="button"
						className={styles.close_button}
						onClick={closeModal}
					>
						<Cross1Icon />
					</button>
				</div>
				<div className={styles.rule_upper_container}>
					<div>
						ルール名：
						<input
							type="text"
							required
							className={styles.rule_name_input}
							{...methods.register("name")}
							onKeyDown={(e) => {
								if (e.key === "Enter") e.preventDefault()
							}}
						/>
						{errorMessages.length > 0 && (
							// TODO: component 化したいかも
							<div className={styles.error_messages}>
								{errorMessages.map((message, index) => (
									<div key={index} className={styles.error_message}>
										{message}
									</div>
								))}
							</div>
						)}
					</div>
					<button
						type="submit"
						className={styles.edit_button}
						disabled={updating || creating}
					>
						{mode === companySmsSendingRuleModalMode.EDIT ? "更新" : "作成"}
					</button>
				</div>
				<div className={styles.table}>
					<div className={styles.table_header}>
						<div className={styles.corner_header}>
							キャンセル種別/お客様種別
						</div>
						<div className={styles.table_cols_header}></div>
					</div>
					{rows.map((_, i) => (
						<CompanySmsSendingRuleRowEditV2
							key={i}
							index={i}
							selectableClaimingConversionPolicyNames={
								rule?.selectableClaimingConversionPolicyNames || []
							}
							selectableEncouragingRereservationPolicyNames={
								rule?.selectableEncouragingRereservationPolicyNames || []
							}
							row={rows[i]}
							register={methods.register}
							setValue={methods.setValue}
							watch={methods.watch}
							errors={methods.formState.errors}
						/>
					))}
				</div>
			</FormProvider>
		</form>
	)
}

export default CompanySmsSendingRuleEditV2
